import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';

const CountUp = ({ end, suffix = '', duration = 2000 }) => {
  const [count, setCount] = useState(0);
  const [started, setStarted] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !started) {
          setStarted(true);
        }
      },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [started]);

  useEffect(() => {
    if (!started) return;
    const startTime = performance.now();
    const step = (now) => {
      const progress = Math.min((now - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(eased * end));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [started, end, duration]);

  return (
    <span ref={ref}>
      {count}{suffix}
    </span>
  );
};

export const Hero = () => {
  return (
    <section className="relative min-h-screen flex items-center" data-testid="hero-section">
      {/* Background Image */}
      <div className="absolute inset-0 z-0">
        <img
          src="/hero.webp"
          alt="Az Akadémia ingatlanközvetítői és céges tréning központ Budapesten"
          className="w-full h-full object-cover opacity-40"
          loading="eager"
          fetchpriority="high"
          width="1920"
          height="1080"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#07516B]/70 via-[#07516B]/50 to-[#404041]" />
      </div>

      <div className="container-custom relative z-10 pt-24 pb-12 md:pt-32 md:pb-20">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="max-w-4xl"
        >
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.6 }}
            className="text-brand-light-green font-mono text-sm tracking-widest uppercase mb-6"
          >
            XXI. századi készségfejlesztés
          </motion.p>

          <h1 className="font-heading font-bold text-white leading-none mb-4">
            <span className="block text-3xl md:text-5xl lg:text-7xl">
              Üdvözöllek az{' '}
              <span className="text-gradient-brand">Akadémián</span>
            </span>
            <span className="block text-lg md:text-2xl lg:text-3xl font-medium text-white/85 mt-3 md:mt-4">
              készségfejlesztő tréningek és ingatlanközvetítői képzés
            </span>
          </h1>

          <p className="text-lg md:text-2xl lg:text-3xl text-white/60 font-heading mb-8 md:mb-12">
            A fejlődés új szintje
          </p>

          <div className="flex flex-col sm:flex-row gap-4">
            <Link
              to="/#kepzesek"
              className="btn-primary flex items-center justify-center gap-2 group"
              data-testid="hero-cta-trainings"
            >
              Workshopok & tréningek
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link
              to="/ingatlan"
              className="btn-outline flex items-center justify-center gap-2"
              data-testid="hero-cta-realestate"
            >
              Ingatlanközvetítői képzés
            </Link>
          </div>
        </motion.div>

        {/* Stats */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 0.8 }}
          className="mt-12 md:mt-24 grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-8"
        >
          {[
            { value: 20, suffix: '+', label: 'év tapasztalat' },
            { value: 5000, suffix: '+', label: 'képzett szakember' },
            { value: 150, suffix: '+', label: 'céges partner' },
            { value: 98, suffix: '%', label: 'elégedettség' },
          ].map((stat) => (
            <div key={stat.label} className="text-center md:text-left">
              <p className="font-heading text-2xl md:text-4xl lg:text-5xl font-bold text-[#FBAF3F]">
                <CountUp end={stat.value} suffix={stat.suffix} duration={2000} />
              </p>
              <p className="text-white/60 text-sm mt-2 uppercase tracking-wide">{stat.label}</p>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};
